<template>
    <section class="section">
        <div class="container">
            <div class="columns">
                <div class="column">
                    <h1 class="title">{{ translate("admin_mats.title") }}</h1>
                </div>
            </div>

            <div class="columns">
                <div class="column is-12">
                    <div class="buttons">
                        <button class="button is-primary" @click="openAddMatModal" :disabled="matsStore.editOrderMode">
                            <span class="icon">
                                <i class="fas fa-plus"></i>
                            </span>
                            <span>{{ translate("admin_mats.add_mat") }}</span>
                        </button>

                        <button
                            class="button"
                            :disabled="!matsStore.changingOrderPossible || matsStore.editOrderMode"
                            @click="enableEditOrderMode"
                        >
                            <span class="icon">
                                <i class="fas fa-random"></i>
                            </span>
                            <span>{{ translate("admin_mats.edit_order") }}</span>
                        </button>

                        <template v-if="matsStore.editOrderMode">
                            <LoadingButton :is-loading="reorderInProgress" class="button is-success" @click="confirmReorder">
                                {{ translate("common.save") }}
                            </LoadingButton>
                            <button class="button is-danger" @click="disableEditOrderMode">
                                {{ translate("common.cancel") }}
                            </button>

                            <p>{{ translate("admin_mats.drag_to_reorder") }}</p>
                        </template>
                    </div>
                </div>
            </div>

            <div v-if="matsStore.isLoading && !matsStore.hasMats" class="columns is-mobile is-centered">
                <LoadingSpinner2 class="has-added-margin" />
            </div>

            <div v-else-if="!matsStore.hasMats" class="columns">
                <div class="column is-12">
                    <Notification class="is-info">
                        <p>{{ translate("admin_mats.no_mats_yet") }}</p>
                    </Notification>
                </div>
            </div>

            <div v-else class="columns">
                <div class="column is-12">
                    <table v-if="matsStore.editOrderMode" class="table is-fullwidth is-hoverable">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>{{ translate("admin_mats.mat_name") }}</th>
                                <th></th>
                            </tr>
                        </thead>

                        <Sortable :list="matsStore.mats" item-key="id" tag="tbody" :options="dragOptions" @update="onTableUpdate">
                            <template v-slot:item="{ element: mat, index }">
                                <tr class="draggable-table-item hoverable-button-container">
                                    <td>
                                        <div>{{ index + 1 }}</div>
                                        <div class="is-size-7 has-text-grey-light">ID: {{ mat.id }}</div>
                                    </td>
                                    <td>{{ mat.name }}</td>
                                    <td>
                                        <div class="buttons is-pulled-right">
                                            <button class="button is-small is-info" disabled>
                                                <span class="icon is-small">
                                                    <i class="fas fa-edit"></i>
                                                </span>
                                                <span>{{ translate("admin_mats.edit") }}</span>
                                            </button>
                                            <button class="button is-small is-danger" disabled>
                                                <span class="icon is-small">
                                                    <i class="fas fa-trash"></i>
                                                </span>
                                                <span>{{ translate("admin_mats.delete") }}</span>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </template>
                        </Sortable>
                    </table>

                    <table v-else class="table is-fullwidth is-hoverable">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>{{ translate("admin_mats.mat_name") }}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(mat, index) in matsStore.mats" :key="mat.id">
                                <td>
                                    <div>{{ index + 1 }}</div>
                                    <div class="is-size-7 has-text-grey-light">ID: {{ mat.id }}</div>
                                </td>
                                <td>{{ mat.name }}</td>
                                <td>
                                    <div class="buttons is-pulled-right">
                                        <button class="button is-small is-info" @click="openEditMatModal(mat)">
                                            <span class="icon is-small">
                                                <i class="fas fa-edit"></i>
                                            </span>
                                            <span>{{ translate("admin_mats.edit") }}</span>
                                        </button>
                                        <button class="button is-small is-danger" @click="handleDeleteMat(mat)">
                                            <span class="icon is-small">
                                                <i class="fas fa-trash"></i>
                                            </span>
                                            <span>{{ translate("admin_mats.delete") }}</span>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <MatFormModal ref="matFormModal" />
    </section>
</template>

<script setup lang="ts">
    import { ref, onMounted } from "vue";
    import MatFormModal from "./MatFormModal.vue";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import { Mat } from "../Common/types";
    import { translate } from "../Common/translations";
    import { useMatsStore } from "./matsStore";
    import { handleError } from "../Common/errorHandler";
    import { successToast } from "../Common/toasts";
    import Notification from "../Common/Notification.vue";
    import { Sortable } from "sortablejs-vue3";
    import { adaptToSortableUpdate, SortableEvent } from "../Common/sortableUtils";
    import mats from "../Api/mats";
    import LoadingButton from "../Common/LoadingButton.vue";
    import { ComponentExposed } from "vue-component-type-helpers";

    const props = defineProps<{
        eventId: number;
    }>();

    const matsStore = useMatsStore();
    matsStore.eventId = props.eventId;

    const matFormModal = ref<ComponentExposed<typeof MatFormModal> | null>(null);

    onMounted(() => {
        return matsStore.fetchMats();
    });

    function openAddMatModal() {
        matFormModal.value?.openForCreate();
    }

    function openEditMatModal(mat: Mat) {
        matFormModal.value?.openForEdit(mat);
    }

    function handleDeleteMat(mat: Mat) {
        const confirmMessage = `${translate("admin_mat.sure_to_remove")} ${mat.name}?`;

        if (!confirm(confirmMessage)) {
            return;
        }

        return matsStore
            .deleteMat(mat.id)
            .then(() => {
                successToast(translate("admin_mats.mat_deleted"));
            })
            .catch(handleError);
    }

    //Edit order mode
    const dragOptions = ref({
        animation: 150,
        scroll: true,
        forceFallback: true,
        bubbleScroll: true,
    });
    const reorderInProgress = ref(false);

    function enableEditOrderMode() {
        matsStore.editOrderMode = true;
    }
    function disableEditOrderMode() {
        matsStore.editOrderMode = false;

        return matsStore.fetchMats();
    }

    function onTableUpdate(event: SortableEvent) {
        matsStore.mats = adaptToSortableUpdate(event, matsStore.mats);
    }

    function confirmReorder() {
        reorderInProgress.value = true;
        return mats
            .confirmReorder(matsStore.eventId, {
                matIds: matsStore.mats.map(m => m.id),
            })
            .then(() => {
                successToast(translate("admin_mats.order_changed"));
                matsStore.editOrderMode = false;
                return matsStore.fetchMats();
            })
            .catch(handleError)
            .finally(() => {
                reorderInProgress.value = false;
            });
    }
</script>

<style scoped></style>
